import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { InternalStandardProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { uniqBy } from "lodash-es";
import qs from "qs";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useRouting } from "JS/React/Hooks/Routes";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { Layout } from "../Layout";
import { MixedContentListing } from "./MixedContentListing";
import { useMixedContent } from "JS/React/Hooks/MixedContent";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import { MixedDataObject } from "JS/Models/MixedConent";
import { useGlobalVideos, useVideos } from "JS/React/Hooks/Video";
import { useCourses, useGlobalCourses } from "JS/React/Hooks/Course/Course";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { RenderIf } from "JS/React/Components/Wrapper/RenderIf";
import { Content, Course } from "JS/Models";

const StyledRoot = styled("div")({
    height: "25vh",
});

const StyledContent = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: `70vh`,
    width: "100%",
    color: `${theme.palette.common.black}`,
}));

export interface MixedContentProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const MixedContent = (props: MixedContentProps) => {
    const { className, ...rest } = props;
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { homeStack } = useGlobalNavStack();

    const [searchQuery, setSearchQuery] = useState(
        homeStack?.searchQuery ? homeStack?.searchQuery : "",
    );
    const {
        loading,
        audiosLoading,
        videosLoading,
        coursesLoading,
        mappedMixedContent,
        scrollTo,
        filePath,
    } = useMixListingHandlers();
    const { setGlobalHomeStack } = useGlobalNavStack();

    useEffect(() => {
        setGlobalHomeStack({
            searchQuery: searchQuery,
        });
    }, [searchQuery]);

    const onBackClick = () => {
        navigate(linkProvider.react.home());
    };

    const isContentLoaded = !(
        loading ||
        audiosLoading ||
        videosLoading ||
        coursesLoading
    );

    return (
        <Layout>
            <StyledRoot {...rest} className={clsx(className)}>
                <AppHeader
                    title="Mixed Content"
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search content by title/description"}
                    canGoBack={true}
                    onBackClick={onBackClick}
                />
                <RenderIf
                    isTrue={isContentLoaded}
                    fallback={
                        <AppBackdropProgress
                            open={true}
                            backdropText={messaging.loader.content}
                        />
                    }
                >
                    <RenderIf
                        isTrue={mappedMixedContent.length > 0}
                        fallback={
                            <StyledContent
                                align="center"
                                variant="h6"
                            >
                                {messaging.common.noItemsFound}
                            </StyledContent>
                        }
                    >
                        <MixedContentListing
                            scrollToIndex={scrollTo}
                            searchQuery={searchQuery}
                            content={mappedMixedContent}
                            filePath={filePath}
                        />
                    </RenderIf>
                </RenderIf>
            </StyledRoot>
        </Layout>
    );
};

const useMixListingHandlers = () => {
    const location = useLocation();

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { filePath = "", scrollTo = "0" } = parsedQueryString;

        return { filePath: filePath.toString(), scrollTo: scrollTo.toString() };
    }, [location?.search]);

    const { loading, mixedContentJson } = useMixedContent(queryParams.filePath);
    const { audios } = useGlobalAudios();
    const { loading: audiosLoading } = useAudios();

    const { videos } = useGlobalVideos();
    const { loading: videosLoading } = useVideos();

    const { courses } = useGlobalCourses();
    const { loading: coursesLoading } = useCourses();

    const mappedMixedContent: MappedListingType[] = useMemo(() => {
        if (mixedContentJson) {
            const filteredAudios =
                audios && mixedContentJson.audios
                    ? audios
                          .filter((a) =>
                              mixedContentJson.audios
                                  .map((a) => a.sku)
                                  .includes(a.sku_id),
                          )
                          .map((audio) => {
                              return {
                                  ...audio,
                                  ...mixedContentJson.audios.find(
                                      (a) => a.sku === audio.sku_id,
                                  ),
                              };
                          })
                    : [];
            const filteredVideos =
                videos && mixedContentJson.videos
                    ? videos
                          .filter((v) =>
                              mixedContentJson.videos
                                  .map((v) => v.sku)
                                  .includes(v.sku_id),
                          )
                          .map((video) => {
                              return {
                                  ...video,
                                  ...mixedContentJson.videos.find(
                                      (v) => v.sku === video.sku_id,
                                  ),
                              };
                          })
                    : [];
            const filteredCourses =
                courses && mixedContentJson.courses
                    ? courses
                          .filter((c) =>
                              mixedContentJson.courses
                                  .map((c) => c.sku)
                                  .includes(c.sku_id),
                          )
                          .map((course) => {
                              return {
                                  ...course,
                                  ...mixedContentJson.courses.find(
                                      (c) => c.sku === course.sku_id,
                                  ),
                              };
                          })
                    : [];
            return uniqBy(
                [...filteredAudios, ...filteredVideos, ...filteredCourses],
                "nid",
            );
        } else return [];
    }, [mixedContentJson, audios, videos, courses]);
    return {
        loading,
        audiosLoading,
        videosLoading,
        coursesLoading,
        mappedMixedContent,
        scrollTo: queryParams.scrollTo,
        filePath: queryParams.filePath,
    };
};

export type MappedListingType =
    | (MixedDataObject & Content)
    | (MixedDataObject & Course);
